// This logic is based on lib/stripe_link/helper.rb in pay-server.
// Since we don't have runtime access to the Yaml config files, we make a best effort pass.
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { parseCerthorseHost, parseMydevHost, parseDeployedHost, parseTunneledHost, ROOT_HOSTNAME_SERVICE_DATA, parseFullstackPRRHost } from './parser';
class StripeLinkError extends Error {
    constructor(message, hostname){
        super(message);
        _define_property(this, "hostname", void 0);
        this.name = 'StripeLinkError';
        this.hostname = hostname;
    }
}
const INTERNAL_SERVICES_ENV_SUFFIX = {
    production: '.corp.stripe.com',
    qa: '.qa.corp.stripe.com',
    preprod: '.preprod.corp.stripe.com'
};
const EXTERNAL_HOST_ENV_PREFIX = {
    production: '',
    edge: 'edge-',
    qa: 'qa-',
    'qa-edge': 'qa-edge-',
    preprod: 'preprod-'
};
const EXTERNAL_HOST_ENV_TO_INTERNAL_HOST_ENV = {
    production: 'production',
    edge: 'production',
    qa: 'qa',
    'qa-edge': 'qa',
    preprod: 'preprod'
};
// Services that use a different name in their production
// subdomains than the underlying service name.
const RENAMED_SERVICES = {
    dashboard: 'manage',
    press: 'mkt',
    link: 'mkt'
};
const isDeployedHost = (hostname)=>Object.keys(ROOT_HOSTNAME_SERVICE_DATA).some((rootHostname)=>hostname.endsWith(`.${rootHostname}`) || hostname === rootHostname);
const isMyDevHost = (hostname)=>hostname.endsWith('-mydev.dev.stripe.me');
const isFullstackPRRHost = (hostname)=>hostname.endsWith('-iso.dev.stripe.me') || hostname.endsWith('-dev.dev.stripe.me') || hostname.endsWith('-corpiso.dev.stripe.me');
const isTunneledHost = (hostname)=>hostname.endsWith('.tunnel.stripe.me');
const isCerthorseHost = (hostname)=>hostname.endsWith('-certhorse.dev.stripe.me');
function isRootHostname(hostname) {
    return hostname in ROOT_HOSTNAME_SERVICE_DATA;
}
// hostname is a production hostname!
const extractServiceData = (hostname)=>{
    if (isRootHostname(hostname)) {
        return ROOT_HOSTNAME_SERVICE_DATA[hostname];
    }
    const match = hostname.match(/([\w_-]+)\.(corp\.)?(stripe\.com|link\.com)$/);
    if (match) {
        const externalSubdomain = match[1];
        const domain = match[3];
        return {
            externalSubdomain,
            serviceName: RENAMED_SERVICES[externalSubdomain] || externalSubdomain,
            domain
        };
    } else {
        throw new Error(`${hostname} is not a valid Stripe production hostname.`);
    }
};
const generateDeployedUrl = ({ externalSubdomain, domain }, targetUrl, currentHost)=>{
    const { pathname, hostname } = targetUrl;
    const { type } = parseDeployedHost(hostname);
    const { env } = parseDeployedHost(currentHost);
    if (type === 'internal') {
        const suffix = INTERNAL_SERVICES_ENV_SUFFIX[EXTERNAL_HOST_ENV_TO_INTERNAL_HOST_ENV[env]];
        return `https://${externalSubdomain}${suffix}${pathname}`;
    } else {
        const prefix = EXTERNAL_HOST_ENV_PREFIX[env];
        if (prefix || externalSubdomain) {
            return `https://${externalSubdomain ? prefix : prefix.replace(/-$/, '')}${externalSubdomain || ''}.${domain}${pathname}`;
        } else {
            return `https://${domain}${pathname}`;
        }
    }
};
const generateMyDevUrl = (serviceName, pathname, currentHost)=>{
    const { user, qualifier } = parseMydevHost(currentHost);
    return `https://${user}-${qualifier}--${serviceName}-mydev.dev.stripe.me${pathname}`;
};
const generateFullstackPRRUrl = (serviceName, pathname, currentHost)=>{
    // The `corp` in type should really be dependent on the whether or not the service is internal or external, but assuming it's the same as the current host is good enough for now :P
    const { user, qualifier, type } = parseFullstackPRRHost(currentHost);
    return `https://${user}-${qualifier}--${serviceName}-${type}.dev.stripe.me${pathname}`;
};
const generateTunneledUrl = (serviceName, pathname, currentHost)=>{
    const { user } = parseTunneledHost(currentHost);
    const normalizedServiceName = serviceName.replace(/_/g, '-');
    return `https://${user}-${normalizedServiceName}.tunnel.stripe.me${pathname}`;
};
const generateCerthorseUrl = (serviceName, pathname, currentHost)=>{
    const { certificationName } = parseCerthorseHost(currentHost);
    return `https://${certificationName}-${serviceName}-certhorse.dev.stripe.me${pathname}`;
};
// url is a production url!
export const stripeLinkForHost = (url, currentHost)=>{
    const parsedURL = new URL(url);
    const { hostname, pathname } = parsedURL;
    const linkedServiceData = extractServiceData(hostname);
    if (isDeployedHost(currentHost)) {
        return generateDeployedUrl(linkedServiceData, parsedURL, currentHost);
    } else if (isMyDevHost(currentHost)) {
        return generateMyDevUrl(linkedServiceData.serviceName, pathname, currentHost);
    } else if (isFullstackPRRHost(currentHost)) {
        return generateFullstackPRRUrl(linkedServiceData.serviceName, pathname, currentHost);
    } else if (isTunneledHost(currentHost)) {
        return generateTunneledUrl(linkedServiceData.serviceName, pathname, currentHost);
    } else if (isCerthorseHost(currentHost)) {
        return generateCerthorseUrl(linkedServiceData.serviceName, pathname, currentHost);
    } else {
        throw new StripeLinkError('hostname is not a recognized Stripe host', hostname);
    }
};
const stripeLink = (url)=>{
    return stripeLinkForHost(url, document.location.hostname);
};
export default stripeLink;

//# sourceMappingURL=index.js.map